import {InlineStack, Icon, Link} from '@shopify/polaris';
import {ExternalSmallIcon} from '@shopify/polaris-icons';
import {parseGid, parseGidWithParams} from '@web-utilities/other';
import {useState} from 'react';
import * as React from 'react';
import {TranslatableResourceType} from 'types';
import {redirectToAdminUrl} from 'utilities/redirect';

const resourceAdminPaths: {[key: string]: string} = {
  [TranslatableResourceType.Collection]: 'collections',
  [TranslatableResourceType.Product]: 'products',
  [TranslatableResourceType.OnlineStoreArticle]: 'articles',
  [TranslatableResourceType.OnlineStoreBlog]: 'blogs',
  [TranslatableResourceType.OnlineStorePage]: 'pages',
  [TranslatableResourceType.OnlineStoreMenu]: 'menus',
  [TranslatableResourceType.OnlineStoreThemeJsonTemplate]: 'themes',
  [TranslatableResourceType.OnlineStoreThemeSectionGroup]: 'themes',
  [TranslatableResourceType.OnlineStoreThemeSettingsCategory]: 'themes',
  [TranslatableResourceType.OnlineStoreThemeLocaleContent]: 'themes',
  [TranslatableResourceType.OnlineStoreThemeSettingsDataSections]: 'themes',
  [TranslatableResourceType.OnlineStoreThemeAppEmbed]: 'themes',
  [TranslatableResourceType.Metaobject]: 'content/entries',
  [TranslatableResourceType.Shop]: '/online_store/preferences',
  [TranslatableResourceType.ShopPolicy]: '/settings/legal',
  [TranslatableResourceType.EmailTemplate]: '/settings/notifications',
  [TranslatableResourceType.PackingSlipTemplate]:
    '/settings/packing_slip_template',
  [TranslatableResourceType.DeliveryProfile]: 'settings/shipping/profiles',
};

interface Props {
  loading: boolean;
  resourceId?: string;
  resourceType: TranslatableResourceType;
  resourceAdminIdType: string | null;
  children: React.ReactNode;
}

export const TitleWrapper = ({
  loading,
  resourceId,
  resourceType,
  resourceAdminIdType,
  children,
}: Props) => {
  const [linkHovered, setLinkHovered] = useState(false);

  const adminRedirectableResourceTypes = Object.keys(resourceAdminPaths);
  if (
    loading ||
    !resourceId ||
    !adminRedirectableResourceTypes.includes(resourceType)
  ) {
    return <>{children}</>;
  }

  let adminUrl = '';
  const adminPath = resourceAdminPaths[resourceType];

  switch (resourceType) {
    case TranslatableResourceType.ShopPolicy:
    case TranslatableResourceType.Shop:
    case TranslatableResourceType.EmailTemplate:
    case TranslatableResourceType.PackingSlipTemplate:
      adminUrl = adminPath;
      break;
    case TranslatableResourceType.OnlineStoreThemeSectionGroup:
    case TranslatableResourceType.OnlineStoreThemeJsonTemplate:
    case TranslatableResourceType.OnlineStoreThemeAppEmbed:
    case TranslatableResourceType.OnlineStoreThemeSettingsCategory: {
      const themeId = parseGidWithParams(resourceId).params.themeId;
      adminUrl = `/${adminPath}/${themeId}/editor`;
      break;
    }
    case TranslatableResourceType.Metaobject:
      adminUrl = `/${adminPath}/${resourceAdminIdType}/${parseGid(resourceId)}`;
      break;
    case TranslatableResourceType.OnlineStoreThemeSettingsDataSections:
      adminUrl = `/${adminPath}/${parseGid(resourceId)}/editor`;
      break;
    case TranslatableResourceType.OnlineStoreThemeLocaleContent:
      adminUrl = `/${adminPath}/${parseGid(resourceId)}/language`;
      break;
    default:
      adminUrl = `/${adminPath}/${parseGid(resourceId)}`;
  }

  return (
    <div
      onMouseEnter={() => setLinkHovered(true)}
      onMouseLeave={() => setLinkHovered(false)}
    >
      <Link
        onClick={() =>
          redirectToAdminUrl({
            adminUrl,
            newContext: true,
          })
        }
        removeUnderline
        monochrome
      >
        <InlineStack gap="100">
          {children}
          {linkHovered && <Icon source={ExternalSmallIcon} />}
        </InlineStack>
      </Link>
    </div>
  );
};
